import React, { useEffect, useState, memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, BrainIcon, CheckCircle2Icon, FlaskConicalIcon, HeartPulseIcon, InfinityIcon, MapIcon, ShieldCheckIcon, SparklesIcon, StarIcon, TargetIcon } from 'lucide-react';
import { CheckoutButton } from './CheckoutButton';
import { CountdownTimer } from './CountdownTimer';
import { ImageWithFallback } from './ImageWithFallback';
import { RecentPurchaseNotification } from './RecentPurchaseNotification';
const mockupUrl = 'https://cdn.magicpatterns.com/uploads/1i3F8oBwJoe1SpJ9uSLvAZ/MOCKUP_PEPTIDOS-2.png';
const volumes = [{
  tomo: 'Tomo 1',
  title: 'Fundamentos',
  copy: 'La base para entender conceptos, referencias y orientación antes de profundizar.',
  icon: MapIcon,
  color: 'text-cyan-300'
}, {
  tomo: 'Tomo 2',
  title: 'Recomposición corporal',
  copy: 'Una vista ordenada de las categorías ligadas al metabolismo y la composición corporal.',
  icon: TargetIcon,
  color: 'text-orange-300'
}, {
  tomo: 'Tomo 3',
  title: 'Hipertrofia',
  copy: 'Conexiones entre crecimiento, recuperación y rendimiento para estudiar con contexto.',
  icon: HeartPulseIcon,
  color: 'text-sky-300'
}, {
  tomo: 'Tomo 4',
  title: 'Recuperación',
  copy: 'Rutas visuales para comprender categorías de tejidos y recuperación física.',
  icon: ShieldCheckIcon,
  color: 'text-emerald-300'
}, {
  tomo: 'Tomo 5',
  title: 'Longevidad',
  copy: 'Un panorama sobre salud celular, energía, piel e inmunidad desde la organización.',
  icon: InfinityIcon,
  color: 'text-fuchsia-300'
}, {
  tomo: 'Tomo 6',
  title: 'Optimización cognitiva',
  copy: 'Mapea categorías relacionadas con enfoque, memoria, estrés y descanso.',
  icon: BrainIcon,
  color: 'text-blue-300'
}];
const bonuses = [{
  number: 'Bono 1',
  title: 'Calculadora Visual de Reconstitución™',
  value: '$37',
  image: 'https://cdn.magicpatterns.com/uploads/w5oh9vCMtRUESBrQHzbgiF/BONO_1_PEPTIDOS.png',
  copy: 'Una referencia visual para comprender equivalencias y concentraciones.'
}, {
  number: 'Bono 2',
  title: 'Manual del Comprador Inteligente™',
  value: '$29',
  image: 'https://cdn.magicpatterns.com/uploads/azLRYtkcbgqsmeGVZHSvwE/BONO_2_PEPTIDOS.png',
  copy: 'Cómo interpretar documentación de calidad con un marco más claro.'
}, {
  number: 'Bono 3',
  title: 'Mapa Maestro de Compatibilidades™',
  value: '$39',
  image: 'https://cdn.magicpatterns.com/uploads/7Ztp4h48unNXydiYVBVVio/BONO_3_PEPTIDOS.png',
  copy: 'Una vista organizada de conexiones y diferencias entre categorías.'
}, {
  number: 'Bono 4',
  title: 'Tracker Biofeedback PRO™',
  value: '$27',
  image: 'https://cdn.magicpatterns.com/uploads/3bpGcmhCUp5impeWL2NZT2/BONO_4_PEPTIDOS.png',
  copy: 'Plantilla para ordenar objetivos, observaciones y seguimiento.'
}, {
  number: 'Bono 5',
  title: 'Atlas Visual de Reconstitución y Jeringas™',
  value: '$24',
  image: 'https://cdn.magicpatterns.com/uploads/p1nWACyxJvYNSE9bEZKAnf/BONO_5_PEPTIDOS.png',
  copy: 'Una guía visual de escalas, equivalencias y ejemplos para facilitar la lectura de referencias.'
}, {
  number: 'Bono 6',
  title: 'Guía de Conservación y Manejo Seguro™',
  value: '$19',
  image: 'https://cdn.magicpatterns.com/uploads/osjWo5ZxwT2dVu84CGEkVw/BONO_6_PEPTIDOS.png',
  copy: 'Criterios generales para reconocer prácticas de conservación y manejo de materiales.'
}];
const testimonials = [{
  name: 'Carlos R.',
  quote: 'Lo que más me sorprendió fue entender el panorama completo. Antes sabía nombres. Ahora entiendo cómo se conecta todo.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ab4ab672-0186-4cd5-b3c0-e4f417b9f532.jpg'
}, {
  name: 'Mariana V.',
  quote: 'Abría Reddit todos los días. Hoy simplemente consulto el mapa.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/932587ce-0712-4171-91f2-38dba83e379a.jpg'
}, {
  name: 'Javier A.',
  quote: 'La Calculadora sola ya vale más que el precio completo.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/df7973b4-6711-406d-9bdc-a282b18bf8f7.jpg'
}, {
  name: 'Luciana P.',
  quote: 'Por primera vez encontré toda la información organizada de una forma lógica.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/2bd78d14-9918-4974-88f1-344635c05bc1.jpg'
}];
const valueRows = [['Sistema Mapa Maestro de Péptidos™', '$97'], ['Calculadora Visual de Reconstitución™', '$37'], ['Manual del Comprador Inteligente™', '$29'], ['Mapa Maestro de Compatibilidades™', '$39'], ['Tracker Biofeedback PRO™', '$27'], ['Atlas Visual de Reconstitución™', '$24'], ['Guía de Conservación y Manejo Seguro™', '$19']];
function SectionHeading({
  eyebrow,
  title,
  children
}: {
  eyebrow?: string;
  title: React.ReactNode;
  children?: React.ReactNode;
}) {
  return <div className="mx-auto max-w-3xl text-center" data-id="element-11" data-mp="161974fq60har">
      {eyebrow && <p className="mb-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-cyan-300" data-id="element-12" data-mp="s2rmwrzgxmxl">
          {eyebrow}
        </p>}
      <h2 className="font-display text-3xl font-bold uppercase leading-[0.98] tracking-tight text-white sm:text-5xl" data-id="element-13" data-mp="w1s5qahy4pkl">
        {title}
      </h2>
      {children && <div className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg" data-id="element-14" data-mp="n3yirq62n8a3">
          {children}
        </div>}
    </div>;
}
function CheckList({
  items,
  compact = false
}: {
  items: string[];
  compact?: boolean;
}) {
  return <ul className={`text-left ${compact ? 'space-y-2' : 'space-y-3'}`} data-id="element-15" data-mp="3viyrr1tmdq7y">
      {items.map(item => <li key={item} className="flex gap-3 text-sm leading-snug text-slate-200 sm:text-base" data-id="element-16" data-mp="4c5olsf9nh0s">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" aria-hidden="true" data-id="element-17" data-mp="262moqm2vjwtq" />
          <span data-id="element-18" data-mp="219jqw1r2e17q">{item}</span>
        </li>)}
    </ul>;
}
export function LandingPage() {
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    const target = document.getElementById('hero-offer');
    if (!target) return undefined;
    const observer = new IntersectionObserver(([entry]) => setShowFloatingCta(!entry.isIntersecting), {
      threshold: 0.15
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);
  const faqs = [['¿Recibo acceso inmediatamente?', 'Sí. Tras confirmar el pago recibirás un correo con acceso inmediato a todo el sistema digital.'], ['¿El acceso es para siempre?', 'Sí. Es un único pago, sin mensualidades ni renovaciones.'], ['¿Está en español?', 'Sí. Todo el contenido fue desarrollado completamente en español.'], ['¿Puedo verlo desde cualquier dispositivo?', 'Sí. Puedes consultar el sistema desde computadora, tablet o celular.'], ['¿Necesito conocimientos previos?', 'No. El Tomo 1 organiza las bases para ayudarte a abordar los demás mapas con contexto.']];
  return <main className="overflow-hidden bg-[#06111d] text-white" data-id="element-19" data-mp="3tykiyubbyqzm">
      <section className="relative border-b border-cyan-400/15 bg-[#06111d] px-4 pb-12 pt-5 sm:px-6 sm:pb-20" data-id="element-20" data-mp="2sjex5ps8xnh">
        <div className="mx-auto max-w-6xl" data-id="element-21" data-mp="7rvzfjt6lsmw">
          <div className="mb-9 flex items-center justify-between border-b border-white/10 pb-4" data-id="element-22" data-mp="7sxf61l573ay">
            <div className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.1em] text-white sm:text-sm" data-id="element-23" data-mp="bsxz21r0g6pe">
              <FlaskConicalIcon className="h-5 w-5 text-cyan-300" data-id="element-24" data-mp="2dkualbd6hgk6" /> Mapa
              Maestro
            </div>
            <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-100" data-id="element-25" data-mp="1uobx0rh0kx9s">
              Edición digital
            </span>
          </div>
          <div className="mx-auto max-w-3xl text-center" data-id="element-26" data-mp="23fvt15u9ot12">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-27" data-mp="1zfu1vqm5ri01">
              <p className="mb-4 font-display text-xs font-bold uppercase tracking-[0.2em] text-cyan-300" data-id="element-28" data-mp="1kqfrqgarcwjl">
                Sistema de Navegación Biológica™
              </p>
              <h1 className="font-display text-[2.7rem] font-bold uppercase leading-[0.89] tracking-tight text-white sm:text-6xl" data-id="element-29" data-mp="1ftp8jk3npkty">
                El mapa que convierte miles de horas de investigación en{' '}
                <span className="text-cyan-300" data-id="element-30" data-mp="2r94a716t90ve">decisiones claras.</span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-xl" data-id="element-31" data-mp="2zq4ubw1h9wdw">
                Toda la información organizada por objetivos para que dejes de
                perder tiempo entre foros, estudios y PDFs, y encuentres
                rápidamente el camino que mejor se adapta a lo que buscas.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.12
          }} className="mx-auto mt-7 w-full max-w-2xl" data-id="element-32" data-mp="3ierxg903gbou">
              <ImageWithFallback src={mockupUrl} alt="Colección digital de Mapa Maestro de Péptidos y sus bonos" className="w-full object-contain" containerClassName="min-h-[280px] rounded-2xl" data-id="element-33" data-mp="565dd9m15xxa" />
            </motion.div>
            <div id="hero-offer" className="mx-auto mt-5 max-w-xl rounded-2xl border border-white/10 bg-slate-950/70 p-4 text-left sm:p-5" data-id="element-34" data-mp="2i22xt9wia0a8">
              <CheckoutButton data-id="element-35" data-mp="290vebxynf407">Quiero mi mapa maestro</CheckoutButton>
              <div className="mt-5 border-t border-white/10 pt-4" data-id="element-36" data-mp="1uda99yi7qupn">
                <div className="flex items-end justify-between gap-4 text-xs font-semibold uppercase tracking-wide" data-id="element-37" data-mp="1uaxiqwye3c0z">
                  <span className="text-slate-300" data-id="element-38" data-mp="1lcc59g7ryn72">Precio promocional</span>
                  <span className="text-orange-300" data-id="element-39" data-mp="1le6ngmrnw17g">Solo quedan 17 cupos</span>
                </div>
                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-700" aria-label="Cupos promocionales casi agotados" data-id="element-40" data-mp="1pbup08nv0s8b">
                  <div className="h-full w-[88%] rounded-full bg-orange-500" data-id="element-41" data-mp="1gcpnqukwyksg" />
                </div>
                <p className="mt-3 text-center text-xs text-slate-400" data-id="element-42" data-mp="wrpjuxj4ypb8">
                  Conversión de moneda automática al finalizar el pago.
                </p>
              </div>
              <CountdownTimer data-id="element-43" data-mp="16qepnrxrcjb4" />
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-slate-300" data-id="element-44" data-mp="11qk1vx9a49ua">
              <span className="flex items-center gap-1.5" data-id="element-45" data-mp="1fq1u6mlsa58e">
                <BadgeCheckIcon className="h-4 w-4 text-cyan-300" data-id="element-46" data-mp="1foidiuw7ts63" />
                Descarga inmediata
              </span>
              <span className="flex items-center gap-1.5" data-id="element-47" data-mp="1ape360xk1oe6">
                <BadgeCheckIcon className="h-4 w-4 text-cyan-300" data-id="element-48" data-mp="1petjemzgmbfm" />
                Acceso de por vida
              </span>
              <span className="flex items-center gap-1.5" data-id="element-49" data-mp="1kj4qh2hy3ssa">
                <BadgeCheckIcon className="h-4 w-4 text-cyan-300" data-id="element-50" data-mp="1yhun6h0urqg4" />
                Garantía 7 días
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#081a29] px-4 py-7 sm:px-6" data-id="element-51" data-mp="21ixskcpp2u13">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-y-6 text-center sm:grid-cols-4" data-id="element-52" data-mp="26hiklousm7eg">
          {[['+300', 'Páginas organizadas'], ['40+', 'Péptidos clasificados'], ['6', 'Mapas estratégicos'], ['100%', 'Digital']].map(([number, label]) => <div key={label} data-id="element-53" data-mp="2k9f79ddozp3g">
              <div className="font-display text-3xl font-bold text-cyan-300 sm:text-4xl" data-id="element-54" data-mp="2k8467lbmsvg1">
                {number}
              </div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-55" data-mp="2p9ti136ohd56">
                {label}
              </div>
            </div>)}
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 sm:py-24" data-id="element-56" data-mp="zt24blrhifn4">
        <SectionHeading eyebrow="El problema invisible" title={<>
              El problema nunca fue la{' '}
              <span className="text-cyan-300" data-id="element-58" data-mp="3i2etuhukpax8">información.</span>
            </>} data-id="element-57" data-mp="zudh2b2wqxqd">
          <p data-id="element-59" data-mp="3dbq3x6yjkm07">Fue intentar aprender un tema complejo sin un mapa.</p>
        </SectionHeading>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2" data-id="element-60" data-mp="39a4i2w414xdv">
          <article className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 sm:p-8" data-id="element-61" data-mp="39acyuz39wmuc">
            <p className="font-display text-sm font-bold uppercase tracking-[0.16em] text-slate-400" data-id="element-62" data-mp="34cuuzn444xt7">
              Sin un sistema
            </p>
            <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight" data-id="element-63" data-mp="trvbxteulym">
              Cada decisión se vuelve una apuesta.
            </h3>
            <p className="mt-4 leading-relaxed text-slate-300" data-id="element-64" data-mp="3s2p7u4ofmmdg">
              Puedes abrir blogs, ver decenas de videos, guardar PDFs y aun así
              no saber cuál es el camino correcto.
            </p>
            <p className="mt-4 text-slate-300" data-id="element-65" data-mp="2gj2zel14ojpl">
              La información existe. El orden, no.
            </p>
          </article>
          <article className="rounded-2xl border border-cyan-300/30 bg-cyan-300/5 p-6 sm:p-8" data-id="element-66" data-mp="3d4szxq9k8r8">
            <p className="font-display text-sm font-bold uppercase tracking-[0.16em] text-cyan-300" data-id="element-67" data-mp="84wjy9dc0ghw">
              Con un mapa
            </p>
            <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight" data-id="element-68" data-mp="3qjx9iz7d70g9">
              Sabes qué estudiar primero.
            </h3>
            <p className="mt-4 leading-relaxed text-slate-200" data-id="element-69" data-mp="n1x2pndjp148">
              La complejidad no desaparece: se organiza. Cada categoría
              encuentra su lugar dentro de una ruta clara.
            </p>
            <p className="mt-4 font-semibold text-cyan-100" data-id="element-70" data-mp="d4942mctj9gs">
              Comprendes primero. Después profundizas.
            </p>
          </article>
        </div>
      </section>

      <section className="bg-[#0a1c2c] px-4 py-16 sm:px-6 sm:py-24" data-id="element-71" data-mp="2xtyiwzdiuv9h">
        <SectionHeading eyebrow="Antes vs. después" title="Deja de investigar al azar." data-id="element-72" data-mp="2xvrqcgvyljij">
          <p data-id="element-73" data-mp="2v98w98rfocdu">La diferencia no es más contenido. Es tener una ruta.</p>
        </SectionHeading>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 lg:grid-cols-2" data-id="element-74" data-mp="2vb3f8hj59ufa">
          <div className="rounded-2xl border border-red-400/20 bg-[#111923] p-6" data-id="element-75" data-mp="2q9wciwvuzlfh">
            <h3 className="font-display text-xl font-bold uppercase text-red-300" data-id="element-76" data-mp="349f5gig3mq77">
              Investigar sin un mapa
            </h3>
            <div className="mt-5 space-y-3 text-sm leading-snug text-slate-300" data-id="element-77" data-mp="2z914tlgtuij7">
              {['Abrir 15 pestañas diferentes.', 'Leer estudios difíciles de conectar.', 'Saltar entre Reddit, Telegram y YouTube.', 'Guardar PDFs que nunca vuelves a abrir.', 'Empezar de cero cada vez.'].map(item => <p key={item} data-id="element-78" data-mp="1sus60v11s650">✕ &nbsp;{item}</p>)}
            </div>
          </div>
          <div className="rounded-2xl border border-cyan-300/25 bg-[#0c2538] p-6" data-id="element-79" data-mp="1ssfhu04p4zji">
            <h3 className="font-display text-xl font-bold uppercase text-cyan-300" data-id="element-80" data-mp="1ntmdax7e0ivh">
              Con el sistema
            </h3>
            <CheckList compact items={['Encuentras la ruta adecuada en minutos.', 'Todo organizado según tu objetivo.', 'Cada tema conecta con el siguiente.', 'Comprendes el panorama antes de profundizar.', 'Avanzas con más criterio.']} data-id="element-81" data-mp="3swrr6yeoc7ju" />
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 sm:py-24" data-id="element-82" data-mp="39xdsy49wwanj">
        <SectionHeading eyebrow="Presentamos" title={<>
              Mapa Maestro de <span className="text-cyan-300" data-id="element-84" data-mp="3iyvi0c5armod">Péptidos™</span>
            </>} data-id="element-83" data-mp="3a009uuip02if">
          <p data-id="element-85" data-mp="u71bv71mbgc7">
            El sistema de navegación visual que organiza el conocimiento por
            objetivos, no por capítulos interminables.
          </p>
        </SectionHeading>
        <div className="mx-auto mt-12 grid max-w-5xl gap-4 md:grid-cols-3" data-id="element-86" data-mp="yapj1omc89wx">
          {[['01', 'Elige tu objetivo', 'Recomposición corporal, hipertrofia, recuperación, longevidad u optimización cognitiva.', TargetIcon], ['02', 'Abre el mapa', 'Cada ruta reúne el contexto esencial en un lugar ordenado.', MapIcon], ['03', 'Conecta el panorama', 'Comprende categorías, relaciones y opciones antes de seguir estudiando.', SparklesIcon]].map(([number, title, copy, Icon]) => {
          const Visual = Icon as typeof TargetIcon;
          return <article key={number as string} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6" data-id="element-87" data-mp="3ldif2xes9ur3">
                <div className="flex items-center justify-between" data-id="element-88" data-mp="3geyr6rgmq8w3">
                  <span className="font-display text-2xl font-bold text-orange-300" data-id="element-89" data-mp="2p8tgvtg04sk2">
                    {number as string}
                  </span>
                  <Visual className="h-6 w-6 text-cyan-300" data-id="element-90" data-mp="2ua8zleupimza" />
                </div>
                <h3 className="mt-8 font-display text-xl font-bold uppercase" data-id="element-91" data-mp="2fiqmppx598k5">
                  {title as string}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300" data-id="element-92" data-mp="26ili1njc4o9o">
                  {copy as string}
                </p>
              </article>;
        })}
        </div>
      </section>

      <section className="bg-[#071927] px-4 py-16 sm:px-6 sm:py-24" data-id="element-93" data-mp="1wknvr5l1d1jp">
        <SectionHeading eyebrow="Incluye 6 mapas estratégicos" title="Un sistema para cada ruta de estudio." data-id="element-94" data-mp="1wmiimpfvb3qf">
          <p data-id="element-95" data-mp="3qxx9xaoj1b8a">Más de 300 páginas organizadas y listas para consultar.</p>
        </SectionHeading>
        <div className="mx-auto mt-12 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-96" data-mp="3vy1qf0pfq5yi">
          {volumes.map(({
          tomo,
          title,
          copy,
          icon: Icon,
          color
        }) => <article key={tomo} className="rounded-2xl border border-white/10 bg-[#0a2032] p-6" data-id="element-97" data-mp="1k825joe3e0cg">
              <div className="flex items-start justify-between gap-4" data-id="element-98" data-mp="1u0hnilpe2oxc">
                <span className="font-display text-xs font-bold uppercase tracking-[0.18em] text-slate-400" data-id="element-99" data-mp="1p99gfwbj7fnc">
                  {tomo}
                </span>
                <Icon className={`h-6 w-6 ${color}`} data-id="element-100" data-mp="22z35ofqy81xl" />
              </div>
              <h3 className="mt-8 font-display text-2xl font-bold uppercase leading-[0.95]" data-id="element-101" data-mp="1y2cotq7bl2b4">
                {title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300" data-id="element-102" data-mp="2cy2ga3v9y7n8">
                {copy}
              </p>
            </article>)}
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 sm:py-24" data-id="element-103" data-mp="ilm1ufsbrn7a">
        <SectionHeading eyebrow="Incluidos hoy sin costo adicional" title={<>
              6 herramientas exclusivas{' '}
              <span className="text-orange-300" data-id="element-105" data-mp="26p8i27xqy6w8">valoradas en $175</span>
            </>} data-id="element-104" data-mp="dlq06n73o7uw">
          <p data-id="element-106" data-mp="21roughf5shrm">
            No son bonos de relleno: son complementos para acompañar la consulta
            del sistema.
          </p>
        </SectionHeading>
        <div className="mx-auto mt-12 grid max-w-6xl gap-5 sm:grid-cols-2" data-id="element-107" data-mp="2bhi4hdznkotr">
          {bonuses.map(bonus => <article key={bonus.number} className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60" data-id="element-108" data-mp="1nsyumu0yg3zk">
              <ImageWithFallback src={bonus.image} alt={`Vista previa de ${bonus.title}`} className="h-auto w-full bg-white object-contain" containerClassName="min-h-[220px] w-full" data-id="element-109" data-mp="1xq3m99dkkrnr" />
              <div className="p-5" data-id="element-110" data-mp="393pamsblup88">
                <div className="flex items-center justify-between gap-3" data-id="element-111" data-mp="346pb7099gkq3">
                  <p className="font-display text-xs font-bold uppercase tracking-widest text-cyan-300" data-id="element-112" data-mp="348s0fi2la9tr">
                    {bonus.number}
                  </p>
                  <p className="text-sm font-bold text-orange-300" data-id="element-113" data-mp="3gpuefzwr2ugz">
                    Valor: {bonus.value}
                  </p>
                </div>
                <h3 className="mt-3 font-display text-xl font-bold uppercase leading-tight" data-id="element-114" data-mp="36qvdl3i8834j">
                  {bonus.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300" data-id="element-115" data-mp="3uhr29rrdyi7k">
                  {bonus.copy}
                </p>
              </div>
            </article>)}
        </div>
      </section>

      <section className="bg-[#0a1d2c] px-4 py-16 sm:px-6 sm:py-24" data-id="element-116" data-mp="3lnmh9oyxo0zy">
        <div className="mx-auto max-w-3xl rounded-3xl border border-cyan-300/30 bg-[#06111d] p-5 sm:p-10" data-id="element-117" data-mp="3poxyb63qrdwj">
          <p className="text-center font-display text-xs font-bold uppercase tracking-[0.18em] text-cyan-300" data-id="element-118" data-mp="h8v1xwi2xbni">
            Todo lo que recibes hoy
          </p>
          <div className="mt-7 overflow-hidden rounded-xl border border-white/10" data-id="element-119" data-mp="vwmofv6j974j">
            <div className="grid grid-cols-[1fr_auto] bg-white/5 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-300" data-id="element-120" data-mp="vuabxxjjnoyv">
              <span data-id="element-121" data-mp="mvf28bhajwqt">Incluye</span>
              <span data-id="element-122" data-mp="mudfvautrnts">Valor</span>
            </div>
            {valueRows.map(([name, value]) => <div key={name} className="grid grid-cols-[1fr_auto] gap-3 border-t border-white/10 px-4 py-3 text-sm text-slate-200" data-id="element-123" data-mp="hwbycud1rwkf">
                <span data-id="element-124" data-mp="85zfr7osmdgx">{name}</span>
                <span className="font-semibold text-white" data-id="element-125" data-mp="84xt05yf02jd">{value}</span>
              </div>)}
            <div className="grid grid-cols-[1fr_auto] border-t border-cyan-300/25 bg-cyan-300/10 px-4 py-4 font-display text-base font-bold uppercase" data-id="element-126" data-mp="43chanxm9uar">
              <span data-id="element-127" data-mp="3qblvrp5zsdlv">Valor total</span>
              <span className="text-cyan-300" data-id="element-128" data-mp="3qerm17qcxlj6">$272 USD</span>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-129" data-mp="3lec92h9zmg1q">
            <p className="text-base text-slate-400" data-id="element-130" data-mp="3ba5amn3hlxs0">
              Hoy no pagarás $272. Ni $97.
            </p>
            <p className="mt-1 font-display text-5xl font-bold text-orange-300 sm:text-6xl" data-id="element-131" data-mp="3g7xdgc5o74az">
              $17 USD
            </p>
            <p className="mt-2 text-sm text-slate-300" data-id="element-132" data-mp="3l8cpn0e7u5rd">
              Pago único · Acceso inmediato · Actualizaciones incluidas
            </p>
          </div>
          <div className="mt-7" data-id="element-133" data-mp="2sjnvxmfhp2pn">
            <CheckoutButton data-id="element-134" data-mp="2skp8xwg2fza0">Acceder ahora por solo $17</CheckoutButton>
            <p className="mt-4 text-center text-xs text-slate-400" data-id="element-135" data-mp="2xhfq6n3lx0x0">
              Cambio de moneda automático · Checkout seguro
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 sm:py-24" data-id="element-136" data-mp="378k3lidmwdpq">
        <SectionHeading eyebrow="Historias de usuarios" title="Cuando tienes un mapa, todo empieza a conectar." data-id="element-137" data-mp="37a4xr6w1dun2" />
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-138" data-mp="2er7om9fk2iix">
          {testimonials.map(({
          name,
          quote,
          image
        }) => <figure key={name} className="rounded-2xl border border-white/10 bg-slate-900/60 p-5" data-id="element-139" data-mp="kbsiiese4szd">
              <div className="flex gap-1 text-orange-300" aria-label="5 de 5 estrellas" data-id="element-140" data-mp="u12ei5qcuvsr">
                {Array.from({
              length: 5
            }).map((_, index) => <StarIcon key={index} className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-141" data-mp="130pc0gfyrhgj" data-mpi={index} />)}
              </div>
              <blockquote className="mt-5 text-sm leading-relaxed text-slate-200" data-id="element-142" data-mp="1czoetie1sivm">
                “{quote}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3" data-id="element-143" data-mp="3erjmzo13i593">
                <ImageWithFallback src={image} alt={`Retrato de ${name}`} className="h-8 w-8 rounded-full object-cover" containerClassName="h-8 w-8 rounded-full" data-id="element-144" data-mp="3ertbf76hs4co" />
                <span className="font-semibold text-white" data-id="element-145" data-mp="1ua2022gxnmba">{name}</span>
              </figcaption>
            </figure>)}
        </div>
      </section>

      <section className="bg-[#0a1d2c] px-4 py-16 sm:px-6" data-id="element-146" data-mp="1fijoqhz0qfy9">
        <div className="mx-auto max-w-4xl rounded-3xl border border-cyan-300/25 bg-[#07111c] p-7 text-center sm:p-12" data-id="element-147" data-mp="1kinxtmxlk8eg">
          <ShieldCheckIcon className="mx-auto h-11 w-11 text-cyan-300" data-id="element-148" data-mp="16imum0tv0ovt" />
          <h2 className="mt-5 font-display text-3xl font-bold uppercase leading-none sm:text-4xl" data-id="element-149" data-mp="1bk2evqo7qrd5">
            Garantía total de 7 días
          </h2>
          <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-slate-300" data-id="element-150" data-mp="wo4pqscpinaf">
            Explora los mapas y herramientas con tranquilidad. Si durante los
            próximos 7 días sientes que no cumple con lo prometido, escríbenos y
            te devolveremos el 100% de tu dinero.
          </p>
          <p className="mt-3 font-semibold text-cyan-100" data-id="element-151" data-mp="2uhpa6j7wwvfl">
            Sin preguntas. Sin complicaciones.
          </p>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 sm:py-24" data-id="element-152" data-mp="2phujafo505uj">
        <SectionHeading eyebrow="Preguntas frecuentes" title="Respuestas claras, antes de empezar." data-id="element-153" data-mp="2kjj6bw2jpf83" />
        <div className="mx-auto mt-10 max-w-3xl space-y-3" data-id="element-154" data-mp="2gipi11l6xyeo">
          {faqs.map(([question, answer], index) => <div key={question} className="rounded-xl border border-white/10 bg-slate-900/60" data-id="element-155" data-mp="3d6vmnoi44qsy" data-mpi={index}>
              <button type="button" onClick={() => setOpenFaq(openFaq === index ? null : index)} className="flex w-full items-center justify-between gap-4 p-5 text-left font-display text-base font-bold uppercase leading-tight text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-300" aria-expanded={openFaq === index} data-id="element-156" data-mp="38bgad0zr72yn">
                <span data-id="element-157" data-mp="1dplgibj1vpws">{question}</span>
                <ArrowRightIcon className={`h-5 w-5 shrink-0 text-cyan-300 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} data-id="element-158" data-mp="1dqn1p8ts5ss8" />
              </button>
              {openFaq === index && <p className="border-t border-white/10 px-5 py-4 text-sm leading-relaxed text-slate-300" data-id="element-159" data-mp="1msctifrkqtqj">
                  {answer}
                </p>}
            </div>)}
        </div>
      </section>

      <section className="border-t border-cyan-300/20 bg-[#04101a] px-4 py-16 pb-28 sm:px-6 sm:py-24" data-id="element-160" data-mp="21mr53v8t70v6">
        <div className="mx-auto max-w-3xl text-center" data-id="element-161" data-mp="26dhcfe0spdsi">
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cyan-300" data-id="element-162" data-mp="26g3rrztxgxla">
            La diferencia está en el orden
          </p>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.92] sm:text-6xl" data-id="element-163" data-mp="2ffqjtv0ror1o">
            Deja de buscar.
            <br data-id="element-164" data-mp="2ke1u26uz53kf" />
            <span className="text-cyan-300" data-id="element-165" data-mp="2kbfmw8bzwq2q">Empieza a navegar.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-300" data-id="element-166" data-mp="1v8s5k5gvoyik">
            El problema nunca fue la falta de información. Fue intentar aprender
            un tema complejo sin un mapa.
          </p>
          <div className="mx-auto mt-8 max-w-xl" data-id="element-167" data-mp="248ync3jm0knj">
            <CheckoutButton data-id="element-168" data-mp="20b10369eg8ms">Quiero acceder ahora por $17 USD</CheckoutButton>
          </div>
          <div className="mt-7 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-slate-400" data-id="element-169" data-mp="1cl6n8op2yk2j">
            <span data-id="element-170" data-mp="17m3lbd4ectys">6 Mapas Estratégicos</span>
            <span data-id="element-171" data-mp="17j7lfj8znv7p">•</span>
            <span data-id="element-172" data-mp="1lcyukqit5d2w">+300 páginas</span>
            <span data-id="element-173" data-mp="1ld8jsbw13g7j">•</span>
            <span data-id="element-174" data-mp="1lejqokkrt42v">6 herramientas</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#020a10] px-5 py-8 pb-28 text-center text-xs leading-relaxed text-slate-500 sm:pb-8" data-id="element-175" data-mp="1gkfhrvry2lz5">
        <div className="mx-auto max-w-3xl" data-id="element-176" data-mp="oust8smxqb5b">
          <p className="font-display font-bold uppercase tracking-wider text-slate-300" data-id="element-177" data-mp="otrf2famh8jd">
            Mapa Maestro de Péptidos™
          </p>
          <p className="mt-3" data-id="element-178" data-mp="jhcbh7ub9i5l">
            Material exclusivamente educativo y organizativo. No constituye
            consejo médico, diagnóstico, tratamiento ni recomendación de uso.
            Consulta a un profesional de la salud cualificado ante cualquier
            decisión relacionada con tu salud.
          </p>
        </div>
      </footer>

      <RecentPurchaseNotification data-id="element-179" data-mp="xif11ukilal9" />

      {showFloatingCta && <motion.div initial={{
      y: 90,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 90,
      opacity: 0
    }} className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-2xl border border-cyan-300/25 bg-[#081a29]/95 p-2 shadow-2xl backdrop-blur sm:bottom-5" data-id="element-180" data-mp="12gqa432zjh2f">
          <CheckoutButton compact data-id="element-181" data-mp="3d9axq1uzkh7g">Acceder por $17 USD</CheckoutButton>
        </motion.div>}
    </main>;
}